'use client';

import * as Popover from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { mergeRefs } from '@/lib/utils/mergeRefs';
import { cn } from './utils';

dayjs.extend(utc);

type TimePickerProps = Omit<
  ComponentProps<'input'>,
  'type' & {
    error?: string | null;
  }
>;

export const TimePicker = ({ className, ref, value, onChange, ...props }: TimePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const combinedRef = mergeRefs(inputRef, ref);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string>('00');
  const [selectedMinute, setSelectedMinute] = useState<string>('00');
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // Initialize with current UTC time or value
  useEffect(() => {
    if (value && typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
      const [h, m] = value.split(':');
      if (h) setSelectedHour(h);
      if (m) setSelectedMinute(m);
    } else {
      // Set to current UTC time if no value
      const now = dayjs().utc();
      setSelectedHour(String(now.hour()).padStart(2, '0'));
      setSelectedMinute(String(now.minute()).padStart(2, '0'));
    }
  }, [value]);

  // Scroll to selected time when popover opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        // Scroll hour list to selected hour
        if (hourListRef.current) {
          const selectedHourElement = hourListRef.current.querySelector(
            `[data-hour="${selectedHour}"]`,
          );
          if (selectedHourElement) {
            selectedHourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }

        // Scroll minute list to selected minute
        if (minuteListRef.current) {
          const selectedMinuteElement = minuteListRef.current.querySelector(
            `[data-minute="${selectedMinute}"]`,
          );
          if (selectedMinuteElement) {
            selectedMinuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }, 50);
    }
  }, [isOpen, selectedHour, selectedMinute]);

  // Generate hours (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

  // Generate minutes (00-59)
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const handleHourClick = (hour: string) => {
    setSelectedHour(hour);
    const newValue = `${hour}:${selectedMinute}`;
    if (onChange) {
      const event = { target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };

  const handleMinuteClick = (minute: string) => {
    setSelectedMinute(minute);
    const newValue = `${selectedHour}:${minute}`;
    if (onChange) {
      const event = { target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };

  const displayValue = value || `${selectedHour}:${selectedMinute}`;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div
          className={cn(
            `relative flex h-11 w-full min-w-0 cursor-pointer items-center rounded-[10px] bg-v2-toggle-neutral-surface
            px-4 py-3 transition-colors`,
            className,
          )}
        >
          <input
            placeholder="00:00"
            type="text"
            className="h-full w-full cursor-pointer bg-transparent text-[13px] leading-[1.5] text-v2-text-secondary outline-none"
            ref={combinedRef}
            value={displayValue}
            readOnly
            {...props}
          />
          <Icon
            size={20}
            name={'Clock'}
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-v2-text-secondary opacity-50"
          />
        </div>
      </Popover.Trigger>
      <Popover.Content
        className="z-50 rounded-[10px] border border-v2-border bg-v2-btn-secondary p-3 shadow-[1px_1px_8px_0px_rgba(0,0,0,0.2)]"
        align="start"
        side="bottom"
        sideOffset={8}
      >
        <div className="flex gap-1">
          {/* Hours column */}
          <div
            ref={hourListRef}
            className="scrollbar-hide flex h-[280px] w-10 flex-col overflow-y-scroll"
          >
            {hours.map((hour) => (
              <button
                key={hour}
                data-hour={hour}
                type="button"
                onClick={() => handleHourClick(hour)}
                className={cn(
                  'flex w-10 shrink-0 items-center justify-center rounded-[10px] p-2 text-[13px] leading-[1.5] transition-colors',
                  selectedHour === hour
                    ? 'bg-v2-surface-tertiary text-v2-text-primary'
                    : 'text-v2-text-secondary hover:bg-v2-surface-tertiary hover:text-v2-text-primary',
                )}
              >
                {hour}
              </button>
            ))}
          </div>

          {/* Minutes column */}
          <div
            ref={minuteListRef}
            className="scrollbar-hide flex h-[280px] w-10 flex-col overflow-y-auto"
          >
            {minutes.map((minute) => (
              <button
                key={minute}
                data-minute={minute}
                type="button"
                onClick={() => handleMinuteClick(minute)}
                className={cn(
                  'flex w-10 shrink-0 items-center justify-center rounded-[10px] p-2 text-[13px] leading-[1.5] transition-colors',
                  selectedMinute === minute
                    ? 'bg-v2-surface-tertiary text-v2-text-primary'
                    : 'text-v2-text-secondary hover:bg-v2-surface-tertiary hover:text-v2-text-primary',
                )}
              >
                {minute}
              </button>
            ))}
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
